// ==================== BASIC TYPES ====================

import {OnboardingProcessStatusEnum} from '~/code/pages/onboarding/ApplicationProgress/components/ApplicationProgress/types'

export enum BusinessStructure {
    COMPANY = 'company',
    SOLE_TRADER = 'sole-trader',
    CHARITY = 'charity',
    PARTNERSHIP = 'partnership',
    LLP = 'llp',
    OTHER = 'other'
}

export enum OnboardingStep {
    START = 0,
    COMPANY_DETAILS = 1,
    COMPANY_CONTACTS = 2,
    OWNERS_DIRECTORS = 3,
    BANK_DETAILS = 4,
    OUTLETS_INFORMATION = 5,
    REVIEW_SUBMIT = 6,
    APPLICATION_PROGRESS = 7
}

// ==================== REVERTS INTERFACES ====================
export interface RevertReason {
    id: string
    name: string
    key: string
    date: string
    category: string
    reason: string
    comment: string
    responsibleTeam: string
    answer: string
    answerDate: string
    files: DocumentInfo[]
}

// ==================== ADDRESS INTERFACES ====================

export interface Address {
    country?: string
    postalCode: string
    addressLine1: string
    addressLine2?: string
    townOrCity: string
    county?: string
    countyOrState?: string
}

export interface ResidentialAddress extends Address {
    houseNameOrNumber?: string
    houseName?: string
    houseNumber?: string
}

export interface StoreAddress extends Address {
    locality?: string
    region?: string
    postcode?: string
}

// ==================== DATE INTERFACE ====================

export interface DateOfBirth {
    year: number
    month: number
    day: number
}

// ==================== COUNTRY INFO INTERFACE ====================

export interface OnboardingCountryInfo {
    digitalCode: string
    ioc: string
    iso: string
    value: string
    institution: string
}

// ==================== VERIFICATION INTERFACES ====================

export interface VerificationRecord {
    firstName: string
    middleName: string
    surname: string
    dateOfBirth: DateOfBirth
    roles: string[]
    residentialAddress: ResidentialAddress
    verified: boolean
}

export interface OfficerVerificationResult {
    addressVerifications: VerificationRecord[]
    idVerifications: VerificationRecord[]
    isVerified?: boolean
    verified?: boolean
    personId?: string
    errors?: string[]
    warnings?: string[]
}

export interface VerificationResponse {
    isVerified: boolean
    uberId?: string | null
    existingClient?: boolean
    addressVerifications?: Verification[]
    idVerifications?: Verification[]
    errors?: string[]
    warnings?: string[]
}

export interface Verification {
    firstName: string
    surname: string
    middleName?: string
    dateOfBirth: DateOfBirth
    gender: string
    roles: string[]
    residentialAddress: ResidentialAddress
    verified: boolean
}

// ==================== BANK ACCOUNT INTERFACES ====================

export interface AccountDetails {
    sortCode: string
    account: string
    iban: string
}

export interface ApplicationBankDetails {
    bic: string
    bank: string
    branch: string
    address: string
    city: string
    postalCode: string
    phoneNumber: string
    country: string
}

export interface ValidationResults {
    accountDetails: AccountDetails
    bankDetails: ApplicationBankDetails
    errors: any[]
    validations: any[]
}

export interface CreditSafeVerificationResults {
    bankAccountNumber: string
    sortCode: string
    accountType: string
    verified: boolean
    bankAccountName: string
    results: any
    isSupportedBank: boolean | null
    creditsafeVerified: boolean
    openBankVerified: boolean | null
    needCreditsafeCheck: boolean | null
}

export interface BankAccountVerificationResponse {
    bankAccountNumber: string
    sortCode: string
    accountName: string
    bankName: string
    bankAccountType: 'personal' | 'business'
    validationResults: ValidationResults
    creditSafeVerificationResults: CreditSafeVerificationResults
    id?: number | null
    openBankVerificationResult?: string
    uploadDocManually?: boolean | null
    openBankingRequestSent?: boolean | null
    openBankingQuestionnaire?: any | null
    sortCodeFoundInBase?: boolean
    fromApi?: boolean | null
    isVerified?: boolean
    verified?: boolean
    accountId?: string
    errors?: string[]
    warnings?: string[]
    requiresManualVerification?: boolean
    creditSafeResults?: any
    sortCodeValid?: boolean
}

export interface BankAccount {
    id?: string | number | null
    bankName: string
    bankLogo?: string
    bankAccountNumber: string
    sortCode: string
    accountName: string
    bankAccountType?: 'business' | 'personal'
    isComplete: boolean
    isVerified: boolean
    isDefault?: boolean
    uploadDocManually?: boolean
    verificationResponse?: BankAccountVerificationResponse
    validationResults?: ValidationResults
    creditSafeVerificationResults?: CreditSafeVerificationResults
    openBankVerificationResult?: string
    openBankingRequestSent?: boolean | null
    openBankingQuestionnaire?: any
    sortCodeFoundInBase?: boolean
    fromApi?: boolean | null
}

export interface BankDetailsFormData {
    accounts: BankAccount[]
    id?: string
}

// ==================== PERSON INTERFACES ====================

export interface PersonBaseData {
    id: string
    title: string
    firstName: string
    middleName?: string
    lastName: string
    surname: string
    dateOfBirth: DateOfBirth
    nationality: string
    emailAddress: string
    phone: string
    telephoneNumber: string
    address: Address
    residentialAddress?: Address
    isComplete: boolean
    placeOfBirth?: string
    gender?: string
    verificationResponse?: VerificationResponse
    officerVerificationResult?: OfficerVerificationResult | VerificationResponse
    documents?: Array<{
        type: string
        name: string
        path: string
    }>
    verificationFailed?: boolean
    uploadDocManually?: boolean
    isVerified?: boolean
    deletionAllowed?: boolean
    preFilledFromApi?: {
        title?: boolean
        firstName?: boolean
        middleName?: boolean
        surname?: boolean
        dateOfBirth?: boolean
        nationality?: boolean
        ownershipPercentage?: boolean
        placeOfBirth?: boolean
        gender?: boolean
        emailAddress?: boolean
        telephoneNumber?: boolean
    }
    idDocumentNumber?: string
    idDocumentExpirationDate?: string
    vulnerability?: any
    uberId?: string | null
    existingClient?: boolean | null
    countryOfResidence?: string
    occupation?: string
}

export interface OwnerData extends PersonBaseData {
    ownershipPercentage: number
    isCompany: boolean
    companyNumber?: string
    agreementSignatory?: boolean
}

export interface DirectorData extends PersonBaseData {
    agreementSignatory: boolean
    position: string
    ownershipPercentage?: number
}

export interface CorporateOwnerData {
    id: string
    companyName: string
    companyNumber: string
    ownershipPercentage: number
    isComplete: boolean
    deletionAllowed: boolean
    isVerified?: boolean
    uploadDocManually?: boolean
    fromApi?: boolean
    officerVerificationResult?: any
    agreementSignatory?: any
    jumioCheckResults?: any
    jumioRequestSent?: any
    preFilledFromApi?: {
        companyName?: boolean
        companyNumber?: boolean
        ownershipPercentage?: boolean
    }
}

// ==================== COMPANY INTERFACES ====================

export interface CompanyDetailsFormData {
    entityType: BusinessStructure | string
    customEntityType?: string
    companyName: string
    companyTradeName: string
    companyNumber?: string
    legalBusinessName?: string
    dateOfCreation?: string
    numberOfEmployees?: string
    charityNumber?: number | null
    charityCommission?: number | null
    id?: string
}

export interface CompanyContactsFormData {
    companyWebsite?: string
    emailAddress: string
    telephoneNumber: string
    address: Address
    id?: string
    preFilledFromApi?: {
        companyWebsite?: boolean
        emailAddress?: boolean
        telephoneNumber?: boolean
        address?: {
            postalCode?: boolean
            addressLine1?: boolean
            addressLine2?: boolean
            townOrCity?: boolean
            county?: boolean
        }
    }
}

export interface CompanyProfile extends CompanyDetailsFormData, CompanyContactsFormData {
    merchantCategoryCode?: string
    merchantCategoryCodeDescription?: string
    natureOfBusiness?: string
    natureOfBusinessDetails?: string
    tradeOutsideEea?: boolean
    bankAccountName?: string
    companyAddress?: Address
    averageTransactionValue?: string
    expectedAnnualTurnover?: string
    expectedMonthlyTransactionValues?: string
    expectedAnnualCardTurnover?: string
    highestTransactionValue?: string
    subscriptionServicePeriod?: string
    freeSubscriptionPeriod?: string
    terminalType?: string
    chargeType?: string
    relationshipType?: string
    storeName?: string
    employeesNumber?: string
    bankAccountType?: string
    registeredOfficeAddress?: Address | null
    directDebit?: string
    comments?: string
    opportunityId?: string
    companyType?: string
    directorsPartnersCount?: number | null
    directDebitAccount?: any
    directDebitBankDetails?: any
    onboardingCountryInfo?: OnboardingCountryInfo
    otherDescription?: string
    countryOfInitialRegistration?: string
    companyNameUnchanged?: string
}

// ==================== DOCUMENT INTERFACE ====================

export interface DocumentInfo {
    type: string
    name: string
    path: string
}

export interface DocumentUploadProgress {
    progress: number
    status: 'uploading' | 'completed' | 'failed'
}

export interface DocumentUploadResult {
    success: boolean
    documentInfo?: DocumentInfo
    error?: string
}

// ==================== STORE/OUTLET INTERFACES ====================

export interface SeasonalBusiness {
    isTrue: boolean
    quarter1: number
    quarter2: number
    quarter3: number
    quarter4: number
}

export interface Face2Face {
    isTrue: boolean
    deliveryTime: string
    courierServiceUsed: boolean
}

export interface BusinessModelQuestions {
    seasonalBusiness: SeasonalBusiness
    face2face: Face2Face
}

export interface FinancialInfo {
    expectedAnnualTurnover: string
    expectedAnnualTurnoverCustomValue: string
    expectedMonthlyTransactionValues: string
    averageTransactionValue: string
    tradeOutsideEea: boolean
    tradingCountriesOutsideEea: string[] | null
    businessModelQuestions: BusinessModelQuestions
    expectedAnnualCardTurnover: string
    highestTransactionValue: string
}

export interface ProcessingMethod {
    description: string
    name: string
    percent: number
}

export interface MccCode {
    code: string
    name: string
    alternativeDescriptions: string
    lineOfBusiness: string
}

export interface MccCategory {
    code: string
    name: string
    mcc: MccCode[]
}

export interface StoreData {
    id?: string
    tradingName: string
    systemDescriptor: string
    settlementsAccount: string
    directDebitAccount?: string
    useDirectDebit: boolean
    industry?: string
    productsYouSell: string
    address: StoreAddress
    sameAsRegistered: boolean
    isSeasonalBusiness?: boolean
    q1Percentage?: number
    q2Percentage?: number
    q3Percentage?: number
    q4Percentage?: number
    cardAnnualTurnover?: string
    averageTransactionValue?: string
    highestTransactionValue?: string
    posPercentage?: number
    motoPercentage?: number
    virtualTerminalPercentage?: number
    paymentLinkPercentage?: number
    paymentAppsPercentage?: number
    storeName?: string
    storeAddress?: StoreAddress
    systemStoreAddress?: StoreAddress
    products?: any
    mccDescription?: string
    bankInfo?: BankAccount
    financialInfo?: FinancialInfo
    processingMethods?: ProcessingMethod[]
    advancePayments?: any[]
    natureOfBusiness?: string
    deliveryAddress?: string
    deliveryInstructions?: string
    deliveryPhoneNumber?: string
    removable?: boolean | null
    deliveryType?: string
    fullDeliveryAddress?: string | null
    subscriptionServicePeriod?: string
    freeSubscriptionPeriod?: string
    paymentsInAdvance?: any
    directDebitBankInfo?: BankAccount
    contactPerson?: any
    createdAutomatically?: boolean | null
}

// ==================== APPLICATION PROGRESS INTERFACES ====================

export interface OnboardingProcessStages {
    applicationSubmission?: OnboardingProcessStatusEnum
    contractPreparation?: OnboardingProcessStatusEnum
    contractSigning?: OnboardingProcessStatusEnum
    underwriting?: OnboardingProcessStatusEnum
    acceptPayments?: OnboardingProcessStatusEnum
}

export interface OnboardingProcessData {
    id: string
    businessKey: string
    status: OnboardingProcessStatusEnum | string
    stages: OnboardingProcessStages
    message?: string
    application?: any
}

interface AdditionalInfo {
    revertReasons: RevertReason[]
}

export interface ReviewAndSignSignatory {
    name: string
    email: string
}

export interface ReviewAndSignInfo {
    signatory: ReviewAndSignSignatory
}

export interface OnboardingProcessStatusResponse {
    activeProcessFound: boolean
    activeProcess: OnboardingProcessData
    previousProcess: OnboardingProcessData | null
    additionalInfo?: AdditionalInfo
    reviewAndSignInfo?: ReviewAndSignInfo
}

// ==================== VALIDATION INTERFACES ====================

export interface PersonValidationError {
    id: string
    verified: boolean
    errors: string[]
}

export interface ApplicationValidationResponse {
    startApplication?: {
        complete: boolean
        errors?: string[]
        description?: string
    }
    ownerAndDirectors?: {
        complete: boolean
        errors?: string[]
        description?: string
        persons: PersonValidationError[]
    }
    bankDetails?: {
        complete: boolean
        errors?: string[]
        description?: string
    }
    outletInformation?: {
        complete: boolean
        errors?: string[]
        description?: string
    }
}

// ==================== APPLICATION STEPS INTERFACES ====================

export interface ApplicationSubStep {
    key: string
    title: string
    route: string
    completed?: boolean
    active?: boolean
    disabled?: boolean
}

export interface ApplicationStep {
    key: string
    title: string
    route?: string
    completed?: boolean
    active?: boolean
    disabled?: boolean
    expanded?: boolean
    subSteps?: ApplicationSubStep[]
}

// ==================== APPLICANT INFO INTERFACE ====================

export interface ApplicantInfo {
    acquisitionChannel: string
    firstName: string
    surname: string
    telephoneNumber: string
    middleName: string
    emailAddress: string
    initiatorEmail: string
    opportunityId: string
    segmentCategory: string
    segmentSubCategory: string
    resellerName: string
}

// ==================== PROCESS SETTINGS INTERFACE ====================

export interface ProcessSettings {
    accountValidation: string
    accountVerification: string
    addressAndIDCheck: string
    automatedMSA: string
    bankDetailScheme: string
    checkCompanyNumberService: string
    country: string
    documentsDependencyOnChecks: any
    getIndividualsInformation: string
    id: string
    mastercardMatch: boolean | null
    needPricing: boolean | null
    partner: string
    pepsAndSanctions: string
    phoneCode: string
    phonePattern: string
    postalCodeAddressRetrieval: string
    preReleaseInspection: boolean | null
    underwriting: boolean | null
    visaMatch: boolean | null
    complianceAutoCheck: string
    institution: string
}

// ==================== DICTIONARY INTERFACES ====================

export interface DictionaryItem {
    code: string
    value: string
    description?: string
}

export interface Dictionary {
    category: string
    items: DictionaryItem[]
}

export interface DictionaryResponse {
    result: Dictionary
}

// ==================== PERSON VERIFICATION INTERFACES ====================

export interface PersonToVerify {
    ownershipPercentage?: number
    title: string
    firstName: string
    middleName?: string
    surname: string
    dateOfBirth: DateOfBirth
    gender: string
    nationality: string
    placeOfBirth: string
    emailAddress: string
    telephoneNumber: string
    residentialAddress: ResidentialAddress
}

export interface VerifyPersonRequest {
    role: 'business-owner' | 'director' | 'individual-shareholder'
    person: PersonToVerify
    companyType?: string
}

export interface AddressVerification {
    firstName: string
    surname: string
    middleName?: string
    dateOfBirth: DateOfBirth
    gender: string
    roles: string[]
    residentialAddress: ResidentialAddress & {
        countyOrState?: string
        houseName?: string
        houseNumber?: string
    }
    verified: boolean
}

export interface IdVerification {
    firstName: string
    surname: string
    middleName?: string
    dateOfBirth: DateOfBirth
    gender: string
    roles: string[]
    residentialAddress: ResidentialAddress & {
        countyOrState?: string
        houseName?: string
        houseNumber?: string
    }
    verified: boolean
}

export interface VerifyPersonResponse {
    isVerified: boolean
    uberId?: string | null
    existingClient?: boolean
    addressVerifications?: AddressVerification[]
    idVerifications?: IdVerification[]
    errors?: string[]
    warnings?: string[]
}

// ==================== BANK ACCOUNT VERIFICATION INTERFACES ====================

export interface BusinessOwner {
    firstName: string
    surname: string
    dateOfBirth: DateOfBirth
    gender: string
    residentialAddress: Address
}

export interface BankAccountVerificationRequest {
    businessStructure: string
    companyNumber?: string
    companyName?: string
    bankAccountNumber: string
    bankAccountType: 'personal' | 'business'
    bankAccountName: string
    sortCode: string
    businessOwner?: BusinessOwner
}

// ==================== SAVE APPLICATION REQUEST INTERFACES ====================

export interface PersonOwner {
    title: string
    firstName: string
    middleName?: string
    surname: string
    dateOfBirth: DateOfBirth
    placeOfBirth: string
    nationality: string
    gender: string
    countryOfResidence?: string
    occupation?: string
    emailAddress: string
    telephoneNumber: string
    residentialAddress: ResidentialAddress
    ownershipPercentage?: number
    deletionAllowed: boolean
    officerVerificationResult?: {
        isVerified?: boolean
        uberId?: string | null
        existingClient?: boolean
        addressVerifications?: any[]
        idVerifications?: any[]
        errors?: string[]
        warnings?: string[]
    }
    uploadDocManually?: boolean
    idDocumentNumber?: string
    idDocumentExpirationDate?: string
    vulnerability?: any
    uberId?: any
    existingClient?: any
}

export interface CorporateEntityShareholder {
    companyName: string
    companyNumber: string
    ownershipPercentage?: number
    deletionAllowed?: boolean
    uploadDocManually?: boolean
    fromApi?: boolean
    officerVerificationResult?: any
    agreementSignatory?: any
    jumioCheckResults?: any
    jumioRequestSent?: any
    isVerified?: boolean
}

export interface CompanyDirector {
    ownershipPercentage?: number
    title: string
    firstName: string
    middleName?: string
    surname: string
    dateOfBirth: DateOfBirth
    placeOfBirth: string
    nationality: string
    gender: string
    countryOfResidence?: string
    occupation?: string
    emailAddress: string
    telephoneNumber: string
    residentialAddress: ResidentialAddress
    deletionAllowed: boolean
    idDocumentNumber?: string
    idDocumentExpirationDate?: string
    vulnerability?: any
    uploadDocManually?: boolean
    officerVerificationResult?: {
        isVerified?: boolean
        uberId?: string | null
        existingClient?: boolean
        addressVerifications?: any[]
        idVerifications?: any[]
        errors?: string[]
        warnings?: string[]
    }
}

export interface SaveApplicationRequest {
    businessStructure: string
    paymentSolution?: string
    isFastTrack: boolean
    isMsaSigned: boolean
    companyProfile: CompanyProfile
    companyDirectors?: CompanyDirector[]
    businessOwners?: PersonOwner[]
    individualShareholders?: PersonOwner[]
    corporateEntityShareholders?: CorporateEntityShareholder[]
    bankAccounts?: BankAccount[]
    documents?: DocumentInfo[]
    currentStep?: string
    version: string
    sellerEmail?: string
    sentToDocusign?: any
    docusignEnvelopeId?: string
    docusignProcessStatus?: any
    applicantInfo: ApplicantInfo
    productVersion: string
    uberId?: any
    refereeUberId?: string
}

// ==================== APPLICATION DATA INTERFACE ====================

export interface OnboardingApplication {
    businessKey: string
    businessStructure: string
    currentStep: string
    companyProfile?: CompanyProfile
    companyDirectors?: PersonBaseData[]
    individualShareholders?: PersonBaseData[]
    bankDetails?: BankDetailsFormData
    applicantInfo: ApplicantInfo
    version: string
    bankAccounts: BankAccount[]
    documents: any[]
    stores: any[]
    businessOwners: any[]
    owners: any[]
    corporateEntityShareholders: any[]
}

// ==================== MAIN ONBOARDING DATA INTERFACE ====================

export interface MerchantOnboardingData {
    businessStructure: string
    paymentSolution: string
    isFastTrack: boolean
    isMsaSigned: boolean
    source: string
    companyProfile: CompanyProfile
    individualShareholders: OwnerData[]
    companyDirectors: DirectorData[]
    documents: DocumentInfo[]
    stores: StoreData[]
    currentStep: string
    version: string
    sellerEmail: string
    sentToDocusign: boolean | null
    docusignEnvelopeId: string
    docusignProcessStatus: string | null
    applicantInfo: ApplicantInfo
    acquisitionChannel: string
    processSettings: ProcessSettings
    bankAccounts: BankAccount[]
    productVersion: string
    uberId: string | null
    refereeUberId: string
    existingClient: boolean
}

// ==================== COMPANY INFO API RESPONSE INTERFACES ====================

export interface CompanyInfoAddress {
    country?: string
    postalCode?: string
    addressLine1?: string
    addressLine2?: string
    locality?: string
    region?: string
    townOrCity?: string
    houseNameOrNumber?: string
    countyOrState?: string
    houseName?: string
    houseNumber?: string
}

export interface CompanyInfoProfile {
    type?: string
    companyName?: string
    companyTradeName?: string
    companyNumber?: string
    companyStatus?: string
    dateOfCreation?: string
    hasBeenLiquidated?: boolean
    hasCharges?: boolean
    hasInsolvencyHistory?: boolean
    jurisdiction?: string
    registeredOfficeAddress?: CompanyInfoAddress
    // not from get company info API
    title?: string
    registrationNumber?: string
    numberOfEmployees?: string
}

export interface CompanyInfoDirector {
    id?: string
    title?: string
    firstName?: string
    middleName?: string
    surname?: string
    dateOfBirth?: DateOfBirth
    placeOfBirth?: string
    nationality?: string
    gender?: string
    countryOfResidence?: string
    occupation?: string
    emailAddress?: string
    telephoneNumber?: string
    residentialAddress?: CompanyInfoAddress
    deletionAllowed?: boolean
    idDocumentNumber?: string
    idDocumentExpirationDate?: string
    vulnerability?: any
    uberId?: string | null
    existingClient?: boolean | null
    isVerified?: boolean | null
}

export interface CompanyInfoIndividualShareholder {
    id?: string
    title?: string
    firstName?: string
    middleName?: string
    surname?: string
    dateOfBirth?: DateOfBirth
    placeOfBirth?: string
    nationality?: string
    gender?: string
    countryOfResidence?: string
    occupation?: string
    emailAddress?: string
    telephoneNumber?: string
    residentialAddress?: CompanyInfoAddress
    deletionAllowed?: boolean
    ownershipPercentage?: number
    idDocumentNumber?: string
    idDocumentExpirationDate?: string
    vulnerability?: any
    uberId?: string | null
    existingClient?: boolean | null
    kind?: string
    isVerified?: boolean | null
}

export interface CompanyInfoCorporateEntityShareholder {
    id?: string
    companyName?: string
    companyNumber?: string
    deletionAllowed?: boolean
    ownershipPercentage?: number
    fromApi?: boolean
    officerVerificationResult?: any
    agreementSignatory?: any
    jumioCheckResults?: any
    jumioRequestSent?: any
    uploadDocManually?: any
    uberId?: string | null
    existingClient?: boolean | null
    isVerified?: boolean | null
}

export interface CompanyInfoResponse {
    uberId?: string | null
    existingClient?: boolean
    companyProfile?: CompanyInfoProfile
    companyDirectors?: CompanyInfoDirector[]
    individualShareholders?: CompanyInfoIndividualShareholder[]
    corporateEntityShareholders?: CompanyInfoCorporateEntityShareholder[] | null
}

// ==================== COMPANY SEARCH RESPONSE INTERFACES ====================

export interface CompanySearchItem {
    addressSnippet?: string
    companyNumber?: string
    companyStatus?: string
    companyType?: string
    dateOfCessation?: string
    dateOfCreation?: string
    description?: string
    title?: string
}

export interface CompanySearchResponse {
    items?: CompanySearchItem[]
    itemsPerPage?: number
    pageNumber?: number
    startIndex?: number
    totalResults?: number
}

// ==================== ONBOARDING STORE INTERFACES ====================

export interface IOnboardingStore {
    // Observable properties
    isLoading: boolean
    isSubmitting: boolean
    isInitialized: boolean
    processStatus: string | undefined
    processStages: OnboardingProcessStages | undefined
    validationResponse: ApplicationValidationResponse | undefined
    currentStep: OnboardingStep
    companyDetails: CompanyDetailsFormData | undefined
    companyContacts: CompanyContactsFormData | undefined
    owners: OwnerData[]
    corporateOwners: CorporateOwnerData[]
    directors: DirectorData[]
    bankDetails: BankDetailsFormData | undefined
    stores: StoreData[]
    mccData: MccCategory[]
    isMccLoading: boolean
    rawApplicationData: any
    skipFetchingExistingData: boolean
    originalCompanyInfo: any
    documentUploadProgress: Map<string, DocumentUploadProgress>
    uploadedDocuments: Map<string, DocumentUploadResult>
    ownersFormChanged: Map<string, boolean>
    directorsFormChanged: Map<string, boolean>
    isNavigatingFromValidation: boolean
    businessStructures: {code: string; value: string}[]
    stepCompletionStatus: {[key in OnboardingStep]: boolean}

    // Computed properties
    processKey: string | undefined
    canProceedToNextStep: boolean
    isCurrentStepValid: boolean

    // Methods
    goToStep(step: OnboardingStep): void
    goToNextStep(): void
    goToPreviousStep(): void
    saveCompanyDetails(data: CompanyDetailsFormData): Promise<void>
    saveCompanyContacts(data: CompanyContactsFormData): Promise<void>
    saveOwner(data: OwnerData): Promise<void>
    updateOwner(id: string, data: Partial<OwnerData>): Promise<void>
    deleteOwner(id: string): Promise<void>
    saveDirector(data: DirectorData): Promise<void>
    updateDirector(id: string, data: Partial<DirectorData>): Promise<void>
    deleteDirector(id: string): Promise<void>
    updateSignatory(directorId: string, isSignatory: boolean): Promise<void>
    saveOwnersAndDirectors(): Promise<{success: boolean; errors?: string[]}>
    validateOwnersAndDirectorsOnly(): Promise<{success: boolean; errors?: string[]}>
    saveBankDetails(data: BankDetailsFormData): Promise<void>
    saveStores(data: StoreData[]): Promise<void>
    submitApplication(): Promise<void>
    loadOnboardingData(processKey?: string): Promise<void>
    updateStepCompletionStatus(): void
    checkSelfOnboardingProcessStatus(): Promise<void>
    initializeSelfOnboardingProcess(): Promise<void>
    startSelfOnboardingApplication(): Promise<void>
}

export type BankVerificationSteps =
    | 'initial'
    | 'missMatch'
    | 'success'
    | 'doc_upload'
    | 'need_doc_upload'
